const OUR_TOKEN_KEY = '_tmw_cart_sync_token'
const PKCE_VERIFIER_KEY = '_tmw_cart_sync_cv'
const STATE_KEY = '_tmw_cart_sync_state'
const STATE_EXPIRY_KEY = '_tmw_cart_sync_state_exp'
const RETURN_URL_KEY = '_tmw_cart_sync_return'
const AUTH_BLOCKED_KEY = '_tmw_cart_sync_blocked'
const TOKEN_ENDPOINT_KEY = '_tmw_cart_sync_te'
const AUTH_IN_PROGRESS_KEY = '_tmw_cart_sync_pending'
const CONSUMED_CODE_KEY = '_tmw_cart_sync_consumed'

const STATE_TTL_MS = 5 * 60 * 1000 // 5 minutes

function base64URLEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

function randomString(length = 64) {
  const arr = new Uint8Array(length)
  crypto.getRandomValues(arr)
  return base64URLEncode(arr)
}

async function sha256(str) {
  const enc = new TextEncoder().encode(str)
  return crypto.subtle.digest('SHA-256', enc)
}

function storeTokenResponse(data, shopId) {
  const token = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    shopId,
  }
  localStorage.setItem(OUR_TOKEN_KEY, JSON.stringify(token))
  return token
}

export function getStoredToken(shopId) {
  try {
    const raw = localStorage.getItem(OUR_TOKEN_KEY)
    if (!raw) return null
    const token = JSON.parse(raw)
    if (shopId && token.shopId && token.shopId !== shopId) {
      localStorage.removeItem(OUR_TOKEN_KEY)
      return null
    }
    return token
  } catch (e) {
    return null
  }
}

export function getValidAccessToken(shopId) {
  const token = getStoredToken(shopId)
  if (!token) return null
  if (Date.now() >= token.expiresAt - 60000) return null
  return token.accessToken
}

export function clearToken() {
  localStorage.removeItem(OUR_TOKEN_KEY)
}

function isAuthBlocked() {
  const blocked = localStorage.getItem(AUTH_BLOCKED_KEY)
  if (!blocked) return false
  if (Date.now() > parseInt(blocked, 10)) {
    localStorage.removeItem(AUTH_BLOCKED_KEY)
    return false
  }
  return true
}

function blockAuth(minutes = 5) {
  localStorage.setItem(AUTH_BLOCKED_KEY, String(Date.now() + minutes * 60000))
}

export function isAuthInProgress() {
  const pending = localStorage.getItem(AUTH_IN_PROGRESS_KEY)
  if (!pending) return false
  if (Date.now() > parseInt(pending, 10)) {
    localStorage.removeItem(AUTH_IN_PROGRESS_KEY)
    return false
  }
  return true
}

function setAuthInProgress() {
  localStorage.setItem(
    AUTH_IN_PROGRESS_KEY,
    String(Date.now() + 2 * 60000),
  )
}

function clearAuthInProgress() {
  localStorage.removeItem(AUTH_IN_PROGRESS_KEY)
}

function isCodeConsumed(code) {
  try {
    const consumed = JSON.parse(
      localStorage.getItem(CONSUMED_CODE_KEY) || '[]',
    )
    return consumed.includes(code)
  } catch (e) {
    return false
  }
}

function markCodeConsumed(code) {
  try {
    // Keep only last 5 codes to avoid unbounded growth
    const consumed = JSON.parse(
      localStorage.getItem(CONSUMED_CODE_KEY) || '[]',
    )
    consumed.push(code)
    localStorage.setItem(
      CONSUMED_CODE_KEY,
      JSON.stringify(consumed.slice(-5)),
    )
  } catch (e) {
    // ignore
  }
}

async function discoverAuthEndpoints() {
  try {
    const resp = await fetch('/.well-known/openid-configuration')
    if (!resp.ok) return null
    return resp.json()
  } catch (e) {
    return null
  }
}

export async function initiateAuth(
  shopId, clientId, redirectUri, forcePrompt = false,
) {
  if (isAuthBlocked() || isAuthInProgress()) return

  const config = await discoverAuthEndpoints()
  if (!config) {
    blockAuth(5)
    return
  }

  const codeVerifier = randomString(64)
  const challenge = base64URLEncode(await sha256(codeVerifier))
  const state = randomString(16)

  localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
  localStorage.setItem(STATE_KEY, state)
  localStorage.setItem(STATE_EXPIRY_KEY, String(Date.now() + STATE_TTL_MS))
  localStorage.setItem(TOKEN_ENDPOINT_KEY, config.token_endpoint)
  localStorage.setItem(RETURN_URL_KEY, window.location.href)
  setAuthInProgress()

  const authParams = {
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: 'openid email customer-account-api:full',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }
  if (!forcePrompt) authParams.prompt = 'none'
  const params = new URLSearchParams(authParams)

  window.location.href = `${config.authorization_endpoint}?${params}`
}

export async function handleAuthCallback(shopId, clientId, redirectUri) {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const error = params.get('error')
  const returnedState = params.get('state')

  // Strip OAuth params from URL immediately
  window.history.replaceState(null, '', window.location.pathname)

  const codeVerifier = localStorage.getItem(PKCE_VERIFIER_KEY)
  const storedState = localStorage.getItem(STATE_KEY)
  const stateExpiry = localStorage.getItem(STATE_EXPIRY_KEY)
  const tokenEndpoint = localStorage.getItem(TOKEN_ENDPOINT_KEY)

  localStorage.removeItem(PKCE_VERIFIER_KEY)
  localStorage.removeItem(STATE_KEY)
  localStorage.removeItem(STATE_EXPIRY_KEY)
  localStorage.removeItem(TOKEN_ENDPOINT_KEY)
  localStorage.removeItem(RETURN_URL_KEY)
  clearAuthInProgress()

  if (error) {
    // prompt=none failed — signal caller to retry with full auth
    if (error === 'login_required' || error === 'interaction_required') {
      return { needsFullAuth: true }
    }
    return null
  }

  const stateExpired = stateExpiry && Date.now() > parseInt(stateExpiry, 10)
  const validCallback =
    code &&
    returnedState === storedState &&
    codeVerifier &&
    tokenEndpoint &&
    !stateExpired &&
    !isCodeConsumed(code)

  if (!validCallback) {
    blockAuth(5)
    return null
  }

  markCodeConsumed(code)

  try {
    const resp = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        redirect_uri: redirectUri,
        code,
        code_verifier: codeVerifier,
      }),
    })

    if (!resp.ok) throw new Error('Token exchange failed')
    const data = await resp.json()
    return storeTokenResponse(data, shopId)
  } catch (e) {
    blockAuth(5)
    return null
  }
}

export async function tryRefreshToken(shopId, clientId) {
  const stored = getStoredToken(shopId)
  if (!stored?.refreshToken) return null

  const config = await discoverAuthEndpoints()
  if (!config) return null

  try {
    const resp = await fetch(config.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: stored.refreshToken,
      }),
    })

    if (!resp.ok) return null
    const data = await resp.json()
    return storeTokenResponse(data, shopId).accessToken
  } catch (e) {
    return null
  }
}
